import React, { useState, useEffect } from 'react';
import { BuildIcon, ContentBlockIcon, DividerIcon, VisualiserIcon, ConnectorIcon, SummaryIcon, SplitIcon, UpArrowIcon, DownArrowIcon, ButtonWidgetIcon } from 'assests/GmsIcons';
import { useComponentRegistry } from '../../contexts/ComponentRegistryContext';
import { useEditor } from '../../contexts/EditorContext';

const styles = {
  container: "tw-bg-gray-50 tw-border tw-border-[#D1D1D6] tw-rounded-sm tw-mb-4",
  header: "tw-py-2 tw-px-4 tw-flex tw-justify-between tw-items-center tw-cursor-pointer",
  headerTitle: "tw-text-sm tw-text-[#6D788C] tw-font-semibold",
  list: "tw-p-2",
  listItem: "tw-flex tw-items-center tw-justify-between tw-py-2 tw-px-2",
  icon: "tw-text-gray-500",
  iconDisabled: "tw-text-gray-300",
  label: "tw-ml-2 tw-text-gray-700",
  labelDisabled: "tw-ml-2 tw-text-gray-400",
  addButton: "tw-text-[#1F7AE4] tw-font-medium tw-text-sm hover:tw-underline focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-ring-opacity-50 tw-px-2 tw-py-1 tw-rounded",
  addButtonDisabled: "tw-text-gray-300 tw-font-medium tw-text-sm tw-cursor-not-allowed tw-px-2 tw-py-1",
  addedText: "tw-text-gray-400 tw-font-medium tw-text-sm",
  comingSoonText: "tw-text-xs tw-text-orange-500 tw-font-medium tw-ml-2"
};

type WidgetItem = {
  id: string;
  label: string;
  icon: JSX.Element;
  enabled: boolean;
  isReusable: boolean;
  isUsed: boolean;
  widgetType: string;
  isMoveable?: boolean;
  currentContainer?: string | null;
  isInThisContainer?: boolean;
}

type WidgetSectionProps = {
  id?: string;
  isCollapsed?: boolean;
  selectedContainerId?: string;
  searchTerm?: string;
  onAddWidget?: (widgetId: string) => void;
}

const getWidgetIcon = (widgetType: string): JSX.Element => {
  switch (widgetType) {
    case 'yourBuild':
      return <BuildIcon />;
    case 'contentBlock':
      return <ContentBlockIcon />;
    case 'divider':
      return <DividerIcon />;
    case 'visualizer':
      return <VisualiserIcon />;
    case 'connector':
      return <ConnectorIcon />;
    case 'summary':
      return <SummaryIcon />;
    case 'split':
      return <SplitIcon />;
    case 'button':
      return <ButtonWidgetIcon />;
    default:
      return <ContentBlockIcon />;
  }
};

const DISABLED_WIDGET_TYPES = ['summary', 'visualizer', 'connector', 'split'];

const WidgetSection = ({ id, isCollapsed, selectedContainerId, searchTerm = '',onAddWidget }: WidgetSectionProps) => {
  const baseId = id || 'widget-section';
  const { getAllComponentsWithUsageInfo, getUsageMap,  registryVersion, getYourBuildContainerOnPage } = useComponentRegistry();
  
  const { editorState } = useEditor();
  
  const getWidgetItems = (): WidgetItem[] => {
    if (!selectedContainerId) return [];
    
    const currentPageId = editorState.activePageId;
    const allComponents = getAllComponentsWithUsageInfo(selectedContainerId, currentPageId || undefined);
    
    const widgetComponents = allComponents.filter(comp => comp.type === 'widget');
    
    return widgetComponents.map(widget => {
      const widgetType = widget.widgetType || '';
      
      const isDisabledWidgetType = DISABLED_WIDGET_TYPES.includes(widgetType);
      
      let isEnabled = !widget.usageStatus.isDisabled && !isDisabledWidgetType;
      
      if (widgetType === 'divider') {
        isEnabled = true;
      }
      

      if (widgetType === 'contentBlock') {
        isEnabled = true;
      }

      if (widgetType === 'button') {
        isEnabled = true;
      }

      if (widgetType === 'yourBuild') {
        const currentPageId = editorState.activePageId;
        const yourBuildContainerOnPage = currentPageId ? getYourBuildContainerOnPage(currentPageId) : null;
        const isOnCurrentPage = !!yourBuildContainerOnPage;
        const isInThisContainer = yourBuildContainerOnPage === selectedContainerId;
        
        return {
          id: widget.id,
          label: widget.name,
          icon: getWidgetIcon(widgetType),
          enabled: !isOnCurrentPage, 
          isReusable: widget.isReusable,
          isUsed: isOnCurrentPage,
          widgetType: widgetType,
          currentContainer: yourBuildContainerOnPage,
          isInThisContainer: isInThisContainer
        };
      }
      
      return {
        id: widget.id,
        label: widget.name,
        icon: getWidgetIcon(widgetType),
        enabled: isEnabled,
        isReusable: widget.isReusable,
        isUsed: widget.usageStatus.isUsed,
        widgetType: widgetType
      };
    });
  };

  const [widgetItems, setWidgetItems] = useState<WidgetItem[]>(getWidgetItems());
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    setWidgetItems(getWidgetItems());
  }, [selectedContainerId, registryVersion, JSON.stringify(getUsageMap()), editorState.componentUsage, editorState.activePageId]);

  useEffect(() => {
    if (isCollapsed !== undefined) {
      setIsExpanded(!isCollapsed);
    }
  }, [isCollapsed]);

  const filteredWidgetItems = searchTerm ? 
    widgetItems.filter(item => 
      item.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.widgetType.toLowerCase().includes(searchTerm.toLowerCase())
    ) : 
    widgetItems;

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleAddWidget = (widgetId: string) => {
    const widget = widgetItems.find(w => w.id === widgetId);
    
    if (!widget || !widget.enabled) return;
    
    if (onAddWidget) {
      onAddWidget(widgetId);
      
      setWidgetItems(prevItems => 
        prevItems.map(item => {
          if (item.id === widgetId) {
            return { ...item, isUsed: true };
          }
          
          return item;
        })
      );
    }
  };

  if (!selectedContainerId) {
    return null;
  }

  if (searchTerm && filteredWidgetItems.length === 0) {
    return (
      <div id={baseId} data-testid={baseId} className={styles.container}>
        <div id={`${baseId}-header`} className={styles.header} onClick={toggleExpand}>
          <h3 id={`${baseId}-title`} className={styles.headerTitle}>Widget</h3>
          <div id={`${baseId}-toggle`}>
            {isExpanded ? <UpArrowIcon /> : <DownArrowIcon />}
          </div>
        </div>

        {isExpanded && (
          <div id={`${baseId}-no-search-results`} className="tw-p-4 tw-text-center tw-text-gray-500">
            {`No widgets found matching "${searchTerm}"`}
          </div>
        )}
      </div>
    );
  }

  return (
    <div id={baseId} data-testid={baseId} className={styles.container}>
      <div id={`${baseId}-header`} className={styles.header} onClick={toggleExpand}>
        <h3 id={`${baseId}-title`} className={styles.headerTitle}>Widget</h3>
        <div id={`${baseId}-toggle`}>
          {isExpanded ? <UpArrowIcon /> : <DownArrowIcon />}
        </div>
      </div>

      {isExpanded && (
        <div id={`${baseId}-list`} className={styles.list}>
          {filteredWidgetItems.map(item => {
            
            const isAvailable = item.enabled;
            
            const isDisabledType = DISABLED_WIDGET_TYPES.includes(item.widgetType);
            
            const isContentBlockUsed = item.widgetType === 'contentBlock' && !isAvailable && item.isUsed;
            
            if (item.widgetType === 'yourBuild') {
              const isOnPage = item.isUsed;
              const isInThisContainer = item.isInThisContainer;

              return (
                <div key={item.id} id={`${baseId}-widget-${item.id}`} className={styles.listItem}>
                  <div id={`${baseId}-widget-${item.id}-info`} className={`tw-flex tw-items-center ${isOnPage ? 'tw-opacity-50' : ''}`}>
                    <span className={isOnPage ? styles.iconDisabled : styles.icon}>
                      {item.icon}
                    </span>
                    <span className={isOnPage ? styles.labelDisabled : styles.label}>
                      {item.label}
                    </span>
                  </div>

                  {isInThisContainer ? (
                    <span id={`${baseId}-widget-${item.id}-added`} className={styles.addedText}>Added</span>
                  ) : isOnPage ? (
                    <button
                      id={`${baseId}-widget-${item.id}-add-btn`}
                      disabled={true}
                      className={styles.addButtonDisabled}
                    >
                      Add
                    </button>
                  ) : (
                    <button
                      id={`${baseId}-widget-${item.id}-add-btn`}
                      onClick={() => handleAddWidget(item.id)}
                      className={styles.addButton}
                      aria-label={`Add ${item.label}`}
                    >
                      Add
                    </button>
                  )}
                </div>
              );
            }
            
            return (
              <div key={item.id} id={`${baseId}-widget-${item.id}`} className={styles.listItem}>
                <div id={`${baseId}-widget-${item.id}-info`} className={`tw-flex tw-items-center ${!isAvailable ? 'tw-opacity-50' : ''}`}>
                  <span className={isAvailable ? styles.icon : styles.iconDisabled}>
                    {item.icon}
                  </span>
                  <span className={isAvailable ? styles.label : styles.labelDisabled}>
                    {item.label}
                  </span>
                </div>

                {isContentBlockUsed ? (
                  <span id={`${baseId}-widget-${item.id}-added`} className={styles.addedText}>Added</span>
                ) : isDisabledType ? (
                  <button
                    id={`${baseId}-widget-${item.id}-add-btn`}
                    disabled={true}
                    className={styles.addButtonDisabled}
                  >
                    Add
                  </button>
                ) : (
                  <button
                    id={`${baseId}-widget-${item.id}-add-btn`}
                    onClick={() => handleAddWidget(item.id)}
                    disabled={!isAvailable}
                    className={isAvailable ? styles.addButton : styles.addButtonDisabled}
                    aria-label={`Add ${item.label}`}
                  >
                    Add
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WidgetSection;